/**
 * The Inbox rail badge: unread patient messages across the org's conversations.
 *
 * While the inbox page is open its list query (polled every 5 s, marked read optimistically) is the truth, so the
 * badge reads that cache. Only while it is open: after the user leaves, that cache stays for minutes but nothing
 * refreshes it. Elsewhere it reads one page of the existing list route (`take=100`, the contract maximum)
 * once, again when the window gets focus, and whenever a `WhatsAppInboxChanged` event invalidates it. There is no
 * timer: off the inbox page the event is what keeps it current.
 */
import { skipToken, useQuery } from '@tanstack/react-query';

import type { Conversation } from '../inbox-types';
import { useInboxPageOpen } from '../notify/inbox-open-thread';
import { INBOX_MAX_TAKE, fetchConversations } from './inbox-api';
import type { ConversationListData } from './inbox-cache';
import { shouldRetryInboxRead } from './inbox-errors';
import { inboxKeys } from './use-whatsapp-inbox';

export const inboxUnreadTotalKey = [...inboxKeys.all, 'unread-total'] as const;

export function sumUnread(conversations: readonly Pick<Conversation, 'unreadCount'>[]): number {
  return conversations.reduce((n, c) => n + (c.unreadCount > 0 ? c.unreadCount : 0), 0);
}

export function useInboxUnreadTotal(enabled: boolean): number {
  // Observes the inbox page's list without ever fetching it (skipToken), and uses it only while the page is open.
  const pageOpen = useInboxPageOpen();
  const pageList = useQuery<ConversationListData>({ queryKey: inboxKeys.list(), queryFn: skipToken });
  const pageItems = pageOpen ? pageList.data?.items : undefined;
  const total = useQuery({
    queryKey: inboxUnreadTotalKey,
    queryFn: async ({ signal }) => sumUnread((await fetchConversations({ take: INBOX_MAX_TAKE, signal })).items),
    enabled: enabled && pageItems === undefined,
    staleTime: 30_000,
    refetchOnWindowFocus: true,
    retry: shouldRetryInboxRead,
    meta: { skipGlobalError: true },
  });
  if (!enabled) return 0;
  if (pageItems) return sumUnread(pageItems);
  return total.data ?? 0;
}
